import React, { useEffect, useState } from 'react';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import { FaTrash } from 'react-icons/fa';
import './RiskCatalog.scss';
import '../../../styles/GenericSiteStyles.scss';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import { RiskItem, RisksRestDataProvider } from '../../../data/RiskCatalogsRestDataProvider';
import { ProjectPhasesRestDataProvider, ProjectPhase } from '../../../data/ProjectPhasesRestDataProvider';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import { UserRoles } from '../../../constants/UserRoles';
import RoleGuard from '../../common/RoleGuard';
import { useTranslation } from 'react-i18next';

const RiskCatalog: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);
  const [risks, setRisks] = useState<RiskItem[]>([]);
  const [phases, setPhases] = useState<ProjectPhase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const provider = React.useMemo(() => new RisksRestDataProvider(), []);
  const phasesProvider = React.useMemo(() => new ProjectPhasesRestDataProvider(), []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      provider.getAll(),
      phasesProvider.getAll()
    ])
      .then(([risksData, phasesData]) => {
        setRisks(risksData);
        setPhases(phasesData);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Helper to get phase name from id
  const getPhaseName = (id: string) => phases.find(p => p.id === id)?.name || '';

  const columns: DetailedListColumn<RiskItem>[] = React.useMemo(() => [
    { key: 'name', header: t('screens:riskCatalog.columns.name'), dataField: 'name', type: 'string', required: true },
    { key: 'impact', header: t('screens:riskCatalog.columns.impact'), dataField: 'impact', type: 'multiline', multiline: true, rows: 3, required: true },
    { key: 'severity', header: t('screens:riskCatalog.columns.severity'), dataField: 'severity', type: 'number', align: 'center', render: r => <span className="badge bg-danger">{r.severity}</span>, required: true },
    { key: 'likelihood', header: t('screens:riskCatalog.columns.likelihood'), dataField: 'likelihood', type: 'number', align: 'center', render: r => <span className="badge bg-warning text-dark">{r.likelihood}</span>, required: true },
    { key: 'owner', header: t('screens:riskCatalog.columns.owner'), dataField: 'owner', type: 'string', required: true },
    { key: 'mitigation', header: t('screens:riskCatalog.columns.mitigation'), dataField: 'mitigation', type: 'multiline', multiline: true, rows: 3, required: true },
    {
      key: 'phase',
      header: t('screens:riskCatalog.columns.phase'),
      dataField: 'phase',
      type: 'select',
      options: phases.map(p => ({ label: p.name, value: p.id })),
      render: r => <span>{getPhaseName(r.phase)}</span>,
      required: true
    },
  ], [phases, t]);

  const handleChange = async (newItems: RiskItem[], action: 'add' | 'edit' | 'delete', changedItem: RiskItem) => {
    try {
      if (action === 'add') {
        await provider.create(changedItem);
      } else if (action === 'edit') {
        await provider.update(changedItem.id, changedItem);
      } else if (action === 'delete') {
        await provider.delete(changedItem.id);
      }
      // Always refresh from API after mutation
      const updated = await provider.getAll();
      setRisks(updated);
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <RoleGuard roles={UserRoles.Manager + "," + UserRoles.Admin} redirectOnDeny>
      <div className="risk-catalog-page">  
        <MainScreenHeader title={t('screens:riskCatalog.title')} />
        {error && <ErrorMessage message={error} />}
        <DetailedListComponent
          dataSource={{ data: risks, setData: setRisks }}
          columns={columns}
          rowKey={r => r.id}
          onChange={handleChange}
          editRole={UserRoles.Manager + "," + UserRoles.Admin}
          addRole={UserRoles.Manager + "," + UserRoles.Admin}
          deleteRole={UserRoles.Manager + "," + UserRoles.Admin}
        />
        {loading && <div style={{ marginTop: 12 }}>{t('common:loading')}</div>}
      </div>
    </RoleGuard>
  );
};

export default RiskCatalog;
